import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Badge, Tabs, Tab, Table, Form, Pagination, Spinner } from 'react-bootstrap';
import { 
  ArrowLeftOutlined, 
  LockOutlined, 
  DollarOutlined, 
  DeleteOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import { message } from 'antd';
import Avatar from '../common/Avatar';
import TasksList from './TasksList';
import { adminAPI } from '../../services/apiService';
import { getTranslatedError } from '../../utils/errorTranslator';
import { formatBalanceHistoryDescription } from '../../utils/balanceHistoryLabels';
import StackedModal from '../common/StackedModal';

const Modal = StackedModal;

const UserDetails = ({ user, currentUser, onBack, onUserUpdated, onDelete }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  
  // Данные для истории баланса
  const [balanceHistory, setBalanceHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPagination, setHistoryPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [balanceForm, setBalanceForm] = useState({
    amount: '',
    reason: '',
    operation: 'add', // 'add' or 'deduct'
  });

  useEffect(() => {
    if (activeTab === 'balance_history' && user) {
      loadBalanceHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, historyPagination.page, user]);

  const loadBalanceHistory = async () => {
    const userId = user.user?.id || user.id;
    if (!userId) return;

    try {
      setHistoryLoading(true);
      const params = {
        userId: userId,
        page: historyPagination.page,
        limit: historyPagination.limit
      };
      
      const data = await adminAPI.getBalanceHistory(params);
      
      if (data.history) {
        setBalanceHistory(data.history);
        setHistoryPagination(prev => ({
          ...prev,
          total: data.total || 0,
          totalPages: data.totalPages || 0
        }));
      } else if (Array.isArray(data)) {
        setBalanceHistory(data);
      } else if (data.data && Array.isArray(data.data)) {
        setBalanceHistory(data.data);
      }
    } catch (err) {
      message.error('Ошибка при загрузке истории баланса: ' + getTranslatedError(err));
    } finally {
      setHistoryLoading(false);
    }
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge bg="success">Активен</Badge>
    ) : (
      <Badge bg="danger">Неактивен</Badge>
    );
  };

  const getRoleBadge = (role) => {
    return role === 'admin' ? (
      <Badge bg="danger">Admin</Badge>
    ) : (
      <Badge bg="info">User</Badge>
    );
  };

  const getOperationBadge = (type) => {
    const types = {
      deposit: 'success',
      withdrawal: 'danger',
      payment: 'warning',
      refund: 'info',
      adjustment: 'secondary'
    };
    
    const labels = {
      deposit: 'Пополнение',
      withdrawal: 'Вывод',
      payment: 'Списание',
      refund: 'Возврат',
      adjustment: 'Корректировка'
    };

    return (
      <Badge bg={types[type] || 'secondary'}>
        {labels[type] || type || 'Неизвестно'}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleString('ru-RU');
    } catch {
      return dateString;
    }
  };

  const handleUpdateStatus = async (userId, isActive, role) => {
    try {
      await adminAPI.updateUserStatus(userId, { isActive, role });
      message.success('Статус пользователя обновлен');
      if (onUserUpdated) onUserUpdated();
    } catch (err) {
      message.error('Ошибка при обновлении статуса: ' + getTranslatedError(err));
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      message.error('Пароли не совпадают');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      message.error('Пароль должен быть не менее 6 символов');
      return;
    }
    try {
      const userId = user.user?.id || user.id;
      await adminAPI.updateUserPassword(userId, passwordForm.newPassword);
      message.success('Пароль успешно изменен');
      setShowPasswordModal(false);
      setPasswordForm({ newPassword: '', confirmPassword: '' });
    } catch (err) {
      message.error('Ошибка при изменении пароля: ' + getTranslatedError(err));
    }
  };

  const handleOpenBalanceModal = () => {
    setBalanceForm({ amount: '', reason: '', operation: 'add' });
    setShowBalanceModal(true);
  };

  const handleUpdateBalance = async (e) => {
    e.preventDefault();
    let amount = parseInt(balanceForm.amount, 10);
    if (isNaN(amount) || amount <= 0) {
      message.error('Введите положительное число');
      return;
    }

    if (balanceForm.operation === 'deduct') {
      const currentBalance = user.user?.requestsBalance || user.requests_balance || 0;
      if (amount > currentBalance) {
        message.error('Нельзя списать больше, чем есть на балансе');
        return;
      }
      amount = -amount;
    }

    try {
      const userId = user.user?.id || user.id;
      await adminAPI.updateUserBalance(userId, {
        amount,
        reason: balanceForm.reason || (amount > 0 ? 'Ручное пополнение администратором' : 'Ручное списание администратором'),
      });
      message.success('Баланс успешно обновлен');
      setShowBalanceModal(false);
      if (onUserUpdated) onUserUpdated();
      // Если мы на вкладке истории, обновляем её
      if (activeTab === 'balance_history') {
        loadBalanceHistory();
      }
    } catch (err) {
      message.error('Ошибка при обновлении баланса: ' + getTranslatedError(err));
    }
  };

  if (!user) return null;

  const userData = user.user || user;
  const userId = userData.id;
  const isActive = userData.is_active !== undefined ? userData.is_active : userData.isActive;
  const userRole = userData.role;

  return (
    <div className="user-details-view">
      <div className="d-flex align-items-center mb-4">
        <Button variant="outline-secondary" className="me-3" onClick={onBack}>
          <ArrowLeftOutlined /> Назад к списку
        </Button>
        <h4 className="mb-0">
          Пользователь: {userData.login}
        </h4>
      </div>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-3"
      >
        <Tab 
          eventKey="info" 
          title={
            <span className="d-flex align-items-center gap-2">
              <InfoCircleOutlined />
              Информация
            </span>
          }
        >
          <Card className="mb-3">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Информация о пользователе</h5>
                <div>
                  {getRoleBadge(userRole)}
                  {' '}
                  {getStatusBadge(isActive)}
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={12} className="mb-3">
                  <div className="d-flex align-items-center gap-3">
                    <Avatar 
                      user={userData} 
                      size={64} 
                      showBorder 
                    />
                    <div>
                      <h5 className="mb-0">
                        {userData.login}
                      </h5>
                      <p className="text-muted mb-0">
                        {(userData.first_name || userData.firstName || '')} {(userData.last_name || userData.lastName || '')}
                      </p>
                    </div>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <p><strong>ID:</strong> {userData.id}</p>
                  <p><strong>Логин:</strong> {userData.login}</p>
                  <p><strong>Имя:</strong> {userData.first_name || userData.firstName || '—'}</p>
                  <p><strong>Фамилия:</strong> {userData.last_name || userData.lastName || '—'}</p>
                  <p><strong>Никнейм:</strong> {userData.nickname || '—'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Email:</strong> {userData.email || '—'}</p>
                  <p><strong>Телефон:</strong> {userData.phone || '—'}</p>
                  <p><strong>Баланс:</strong> {userData.requestsBalance || userData.requests_balance || 0} запросов</p>
                  <p><strong>Создан:</strong> {new Date(userData.created_at || userData.createdAt).toLocaleString('ru-RU')}</p>
                  <p><strong>Обновлен:</strong> {new Date(userData.updated_at || userData.updatedAt).toLocaleString('ru-RU')}</p>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>
              <h5 className="mb-0">Статистика</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <h6>Задачи:</h6>
                  <p>Всего: {user.statistics?.tasks?.total || 0}</p>
                  <p>Завершено: {user.statistics?.tasks?.completed || 0}</p>
                  <p>Неудачных: {user.statistics?.tasks?.failed || 0}</p>
                  <p>Ожидающих: {user.statistics?.tasks?.pending || 0}</p>
                  <p>В обработке: {user.statistics?.tasks?.processing || 0}</p>
                </Col>
                <Col md={6}>
                  <h6>Заявки:</h6>
                  <p>Всего: {user.statistics?.claims?.total || 0}</p>
                  <p>Завершено: {user.statistics?.claims?.completed || 0}</p>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <div className="d-flex gap-2 mb-3 flex-wrap">
            <Button
              variant="warning"
              onClick={() => {
                const newStatus = !isActive;
                handleUpdateStatus(userId, newStatus, userRole);
              }}
            >
              {isActive ? 'Деактивировать' : 'Активировать'}
            </Button>
            <Button
              variant="info"
              onClick={() => {
                const newRole = userRole === 'admin' ? 'user' : 'admin';
                handleUpdateStatus(userId, isActive, newRole);
              }}
            >
              Изменить роль на {userRole === 'admin' ? 'User' : 'Admin'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowPasswordModal(true)}
            >
              <LockOutlined /> Изменить пароль
            </Button>
            <Button
              variant="success"
              onClick={handleOpenBalanceModal}
            >
              <DollarOutlined /> Изменить баланс
            </Button>
            <Button
              variant="danger"
              onClick={() => onDelete(userData)}
              disabled={userId === currentUser?.id}
            >
              <DeleteOutlined /> Удалить пользователя
            </Button>
          </div>

          {user.statistics?.recentTasks && user.statistics.recentTasks.length > 0 && (
            <Card>
              <Card.Header>
                <h5 className="mb-0">Последние задачи</h5>
              </Card.Header>
              <Card.Body>
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>ID задачи</th>
                      <th>Тип</th>
                      <th>Статус</th>
                      <th>Дата создания</th>
                    </tr>
                  </thead>
                  <tbody>
                    {user.statistics.recentTasks.map((task) => (
                      <tr key={task.id}>
                        <td>{task.id}</td>
                        <td>{task.task_type}</td>
                        <td>
                          <Badge bg={
                            task.status === 'completed' ? 'success' :
                            task.status === 'failed' ? 'danger' :
                            task.status === 'processing' ? 'warning' : 'secondary'
                          }>
                            {task.status}
                          </Badge>
                        </td>
                        <td>{new Date(task.created_at).toLocaleString('ru-RU')}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </Tab>

        <Tab 
          eventKey="protocols" 
          title={
            <span className="d-flex align-items-center gap-2">
              <FileTextOutlined />
              Протоколы
            </span>
          }
        >
          <TasksList selectedUserId={userId} />
        </Tab>

        <Tab 
          eventKey="balance_history" 
          title={
            <span className="d-flex align-items-center gap-2">
              <HistoryOutlined />
              История баланса
            </span>
          }
        >
          {historyLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Тип</th>
                      <th>Сумма</th>
                      <th>Баланс до/после</th>
                      <th>Описание</th>
                      <th>Дата</th>
                    </tr>
                  </thead>
                  <tbody>
                    {balanceHistory.map((item) => {
                      const operation = item.operationType || item.operation_type || item.type;
                      const balanceBefore = item.balanceBefore ?? item.balance_before;
                      const balanceAfter = item.balanceAfter ?? item.balance_after;
                      const description = formatBalanceHistoryDescription(item);
                      const created = item.createdAt || item.created_at;

                      return (
                        <tr key={item.id}>
                          <td>
                            {getOperationBadge(operation)}
                          </td>
                          <td>
                            <span className={item.amount > 0 ? 'text-success' : 'text-danger'}>
                              {item.amount > 0 ? '+' : ''}{item.amount}
                            </span>
                          </td>
                          <td>
                            {balanceBefore !== undefined && balanceAfter !== undefined ? (
                              <small className="text-muted">
                                {balanceBefore} → {balanceAfter}
                              </small>
                            ) : '—'}
                          </td>
                          <td>{description || '—'}</td>
                          <td>{formatDate(created)}</td>
                        </tr>
                      );
                    })}
                    {balanceHistory.length === 0 && (
                      <tr>
                        <td colSpan="5" className="text-center py-4 text-muted">
                          История пуста
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {historyPagination.totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    <Pagination.First
                      disabled={historyPagination.page === 1}
                      onClick={() => setHistoryPagination({ ...historyPagination, page: 1 })}
                    />
                    <Pagination.Prev
                      disabled={historyPagination.page === 1}
                      onClick={() => setHistoryPagination({ ...historyPagination, page: historyPagination.page - 1 })}
                    />
                    {[...Array(historyPagination.totalPages)].map((_, i) => {
                      const page = i + 1;
                      if (
                        page === 1 ||
                        page === historyPagination.totalPages ||
                        (page >= historyPagination.page - 2 && page <= historyPagination.page + 2)
                      ) {
                        return (
                          <Pagination.Item
                            key={page}
                            active={page === historyPagination.page}
                            onClick={() => setHistoryPagination({ ...historyPagination, page })}
                          >
                            {page}
                          </Pagination.Item>
                        );
                      }
                      return null;
                    })}
                    <Pagination.Next
                      disabled={historyPagination.page === historyPagination.totalPages}
                      onClick={() => setHistoryPagination({ ...historyPagination, page: historyPagination.page + 1 })}
                    />
                    <Pagination.Last
                      disabled={historyPagination.page === historyPagination.totalPages}
                      onClick={() => setHistoryPagination({ ...historyPagination, page: historyPagination.totalPages })}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Tab>
      </Tabs>

      {/* Модальное окно изменения пароля */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Изменить пароль</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdatePassword}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Новый пароль *</Form.Label>
              <Form.Control
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                required
                minLength={6}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Подтверждение пароля *</Form.Label>
              <Form.Control
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                required
                minLength={6}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
              Отмена
            </Button>
            <Button variant="primary" type="submit">
              Изменить
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Модальное окно изменения баланса */}
      <Modal show={showBalanceModal} onHide={() => setShowBalanceModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Изменить баланс</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateBalance}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Тип операции</Form.Label>
              <div>
                <Form.Check
                  inline
                  type="radio"
                  label="Пополнить (+)"
                  name="balanceOperation"
                  id="userDetailsBalanceAdd"
                  checked={balanceForm.operation === 'add'}
                  onChange={() => setBalanceForm({ ...balanceForm, operation: 'add' })}
                  className="text-success fw-bold"
                />
                <Form.Check
                  inline
                  type="radio"
                  label="Списать (-)"
                  name="balanceOperation"
                  id="userDetailsBalanceDeduct"
                  checked={balanceForm.operation === 'deduct'}
                  onChange={() => setBalanceForm({ ...balanceForm, operation: 'deduct' })}
                  className="text-danger fw-bold"
                />
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Количество запросов</Form.Label>
              <Form.Control
                type="number"
                min="1"
                step="1"
                value={balanceForm.amount}
                onChange={(e) => setBalanceForm({ ...balanceForm, amount: e.target.value })}
                required
                placeholder="100"
              />
              <Form.Text className="text-muted">
                {balanceForm.operation === 'deduct' 
                  ? `Будет списано ${balanceForm.amount || 0} запросов. Текущий баланс: ${user.user?.requestsBalance || user.requests_balance || 0}`
                  : `Будет начислено ${balanceForm.amount || 0} запросов.`
                }
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Причина</Form.Label>
              <Form.Control
                type="text"
                value={balanceForm.reason}
                onChange={(e) => setBalanceForm({ ...balanceForm, reason: e.target.value })}
                placeholder={balanceForm.operation === 'add' ? "Бонус" : "Корректировка"}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowBalanceModal(false)}>
              Отмена
            </Button>
            <Button 
              variant={balanceForm.operation === 'add' ? 'success' : 'danger'} 
              type="submit"
            >
              {balanceForm.operation === 'add' ? 'Пополнить' : 'Списать'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default UserDetails;
